import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PostedBifurcation } from "../components/PostedBifurcation";
import {
  Instagram,
  Sparkles,
  Play,
  Loader2,
  Upload,
  ClipboardList,
  Download,
  Search,
  ExternalLink,
  RefreshCw,
  Filter,
  Users,
  Hash,
  AtSign,
  Heart,
  MessageCircle,
  Eye,
  Zap,
  Film,
  Layers,
  Image as ImageIcon,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateField } from "@/components/DateField";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CalendarClock, Play as PlayIcon, FolderOpen, Save, Plus } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POST_TYPE_ICONS = {
  Reel: Film,
  Carousel: Layers,
  Static: ImageIcon,
  Video: Play,
};

function PostTypePill({ type }) {
  const Icon = POST_TYPE_ICONS[type] || ImageIcon;
  const cls =
    type === "Reel"
      ? "pill-reel"
      : type === "Carousel-Video"
        ? "pill-reel"
        : type === "Carousel"
          ? "pill-carousel"
          : type === "Static"
            ? "pill-static"
            : "pill-video";
  return (
    <span className={`pill ${cls}`} data-testid={`post-type-${type?.toLowerCase()}`}>
      <Icon size={11} />
      {type}
    </span>
  );
}

function StatCard({ label, value, icon: Icon, accent = "amber" }) {
  const accentColor =
    accent === "amber"
      ? "text-pink-600"
      : accent === "pink"
        ? "text-pink-600"
        : accent === "purple"
          ? "text-violet-600"
          : "text-emerald-600";
  return (
    <div
      className="bg-white border border-slate-200 rounded-xl p-5 flex items-start justify-between"
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono">
          {label}
        </div>
        <div className={`mt-2 text-3xl font-bold font-mono ${accentColor}`}>
          {value}
        </div>
      </div>
      <div className={`p-2 rounded-lg bg-slate-50 ${accentColor}`}>
        <Icon size={18} />
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [inputMode, setInputMode] = useState("paste");
  const [handlesText, setHandlesText] = useState("");
  const [keywordsText, setKeywordsText] = useState("");
  const [matchMode, setMatchMode] = useState("substring");
  const [postsLimit, setPostsLimit] = useState(10);
  const [running, setRunning] = useState(false);
  const [job, setJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [filterType, setFilterType] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [captionPost, setCaptionPost] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [groups, setGroups] = useState([]);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [timelinePreset, setTimelinePreset] = useState("all"); // all|7d|30d|90d|custom
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const pollingRef = useRef(null);
  const fileInputRef = useRef(null);

  const enabledSchedules = useMemo(
    () => (schedules || []).filter((s) => s.enabled),
    [schedules]
  );

  useEffect(() => {
    loadJobs();
    loadSchedules();
    loadGroups();
    return () => pollingRef.current && clearInterval(pollingRef.current);
  }, []);

  const loadJobs = async () => {
    try {
      const { data } = await axios.get(`${API}/scrape/jobs`);
      setJobs(data.jobs || []);
    } catch (e) {
      console.error(e);
    }
  };

  const loadGroups = async () => {
    try {
      const { data } = await axios.get(`${API}/groups`);
      setGroups(data.groups || []);
    } catch (e) {
      console.error(e);
    }
  };

  const applyGroup = (g) => {
    setHandlesText((g.handles || []).join("\n"));
    if ((g.keywords || []).length) {
      setKeywordsText((g.keywords || []).join("\n"));
    }
    setInputMode("paste");
    toast.success(`Loaded group "${g.name}" · ${g.handles.length} handles`);
  };

  const saveGroup = async () => {
    const name = groupName.trim();
    if (!name) {
      toast.error("Please enter a group name");
      return;
    }
    if (!handles.length) {
      toast.error("Add some handles first");
      return;
    }
    try {
      await axios.post(`${API}/groups`, {
        name,
        handles,
        keywords,
      });
      toast.success(`Saved group "${name}"`);
      setGroupDialogOpen(false);
      setGroupName("");
      loadGroups();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save group");
    }
  };

  const deleteGroup = async (id, name) => {
    try {
      await axios.delete(`${API}/groups/${id}`);
      toast.success(`Deleted "${name}"`);
      loadGroups();
    } catch (e) {
      toast.error("Could not delete group");
    }
  };

  const loadSchedules = async () => {
    try {
      const { data } = await axios.get(`${API}/scrape/schedules`);
      setSchedules(data.schedules || []);
    } catch (e) {
      console.error(e);
    }
  };

  const currentSchedule = useMemo(() => {
    if (!job) return null;
    // Match schedule that has same handles+keywords+mode+limit and is enabled
    return schedules.find(
      (s) =>
        s.enabled &&
        JSON.stringify([...(s.handles || [])].sort()) ===
          JSON.stringify([...(job.handles || [])].sort()) &&
        JSON.stringify([...(s.keywords || [])].sort()) ===
          JSON.stringify([...(job.keywords || [])].sort()) &&
        s.match_mode === job.match_mode &&
        s.posts_limit === job.posts_limit
    );
  }, [job, schedules]);

  const toggleSchedule = async () => {
    if (!job) return;
    try {
      if (currentSchedule) {
        await axios.delete(`${API}/scrape/schedule/${currentSchedule.id}`);
        toast.success("Daily schedule disabled");
      } else {
        await axios.post(`${API}/scrape/schedule`, { job_id: job.id });
        toast.success("Scheduled: daily at 10:00 IST");
      }
      loadSchedules();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update schedule");
    }
  };

  const parseTextToList = (text) =>
    text
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);

  const handles = useMemo(() => parseTextToList(handlesText), [handlesText]);
  const keywords = useMemo(() => parseTextToList(keywordsText), [keywordsText]);

  const uploadParseFile = async (f) => {
    if (!f) return;
    const name = (f.name || "").toLowerCase();
    if (!name.endsWith(".csv") && !name.endsWith(".xlsx") && !name.endsWith(".xls")) {
      toast.error("Please drop a CSV or Excel file");
      return;
    }
    const form = new FormData();
    form.append("file", f);
    try {
      const { data } = await axios.post(`${API}/scrape/parse-file`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.handles?.length) setHandlesText(data.handles.join("\n"));
      if (data.keywords?.length) setKeywordsText(data.keywords.join("\n"));
      toast.success(
        `Loaded ${data.handles?.length || 0} handles, ${data.keywords?.length || 0} keywords`
      );
      setInputMode("paste");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to parse file");
    }
  };

  const handleFileUpload = async (e) => {
    const f = e.target.files?.[0];
    await uploadParseFile(f);
    e.target.value = "";
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const f = e.dataTransfer?.files?.[0];
    await uploadParseFile(f);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const computedRange = useMemo(() => {
    // Returns {from_date, to_date} strings in YYYY-MM-DD or empty
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (timelinePreset === "all") return { from_date: "", to_date: "" };
    if (timelinePreset === "custom") return { from_date: fromDate, to_date: toDate };
    const days = timelinePreset === "7d" ? 7 : timelinePreset === "30d" ? 30 : 90;
    const start = new Date(today);
    start.setDate(start.getDate() - days);
    return { from_date: iso(start), to_date: iso(today) };
  }, [timelinePreset, fromDate, toDate]);

  const startScrape = async () => {
    if (!handles.length) {
      toast.error("Please add at least one Instagram handle");
      return;
    }
    if (!keywords.length) {
      toast.error("Please add at least one keyword");
      return;
    }
    const range = computedRange;
    if (range.from_date && range.to_date && range.from_date > range.to_date) {
      toast.error("From date must be before To date");
      return;
    }
    setRunning(true);
    setJob(null);
    try {
      const { data } = await axios.post(`${API}/scrape/start`, {
        handles,
        keywords,
        match_mode: matchMode,
        posts_limit: postsLimit,
        from_date: range.from_date || null,
        to_date: range.to_date || null,
      });
      toast.success("Scraper started");
      pollJob(data.job_id);
    } catch (err) {
      setRunning(false);
      toast.error(err.response?.data?.detail || "Failed to start scraper");
    }
  };

  const pollJob = (jobId) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    const fetch = async () => {
      try {
        const { data } = await axios.get(`${API}/scrape/job/${jobId}`);
        setJob(data);
        if (data.status === "completed" || data.status === "failed") {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setRunning(false);
          loadJobs();
          if (data.status === "completed") {
            toast.success(`Done! ${data.matched_count || 0} matched posts`);
          } else {
            toast.error(`Job failed: ${data.error || "unknown error"}`);
          }
        }
      } catch (e) {
        console.error(e);
      }
    };
    fetch();
    pollingRef.current = setInterval(fetch, 3000);
  };

  const loadJob = async (id) => {
    try {
      const { data } = await axios.get(`${API}/scrape/job/${id}`);
      setJob(data);
      if (data.status === "running" || data.status === "queued") {
        setRunning(true);
        pollJob(id);
      }
    } catch (e) {
      toast.error("Failed to load job");
    }
  };

  const exportExcel = async () => {
    if (!job?.id) return;
    const t = toast.loading("Preparing Excel…");
    try {
      const res = await axios.get(`${API}/scrape/export/${job.id}`, { responseType: "blob" });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `quick_scan_${job.id.slice(0, 8)}.xlsx`;
      a.click();
      URL.revokeObjectURL(objUrl);
      toast.success("Excel downloaded", { id: t });
    } catch (e) {
      let detail = e.response?.data?.detail || "Export failed";
      if (e.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text()).detail || detail; } catch {}
      }
      toast.error(detail, { id: t });
    }
  };

  const filteredResults = useMemo(() => {
    let rows = job?.results || [];
    if (filterType !== "all") {
      rows = rows.filter((r) => r.post_type === filterType);
    }
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      rows = rows.filter(
        (r) =>
          r.handle?.toLowerCase().includes(q) ||
          r.caption?.toLowerCase().includes(q) ||
          (r.matched_keywords || []).some((k) => k.toLowerCase().includes(q))
      );
    }
    return rows;
  }, [job, filterType, searchTerm]);

  const summary = useMemo(() => job?.summary || [], [job]);
  const isRunning = running && job && (job.status === "queued" || job.status === "running");

  const stats = useMemo(() => {
    const r = job?.results || [];
    return {
      matched: r.length,
      unique: summary.length,
      reels: r.filter((p) => p.post_type === "Reel").length,
      carousels: r.filter((p) => p.post_type === "Carousel" || p.post_type === "Carousel-Video").length,
      staticPosts: r.filter((p) => p.post_type === "Static").length,
      videos: r.filter((p) => p.post_type === "Video").length,
      likes: r.reduce((a, b) => a + (b.likes || 0), 0),
      comments: r.reduce((a, b) => a + (b.comments || 0), 0),
    };
  }, [job, summary]);

  return (
    <div className="min-h-screen bg-[#F8FAFC] grain relative">
      <div className="relative z-10 max-w-[1600px] mx-auto p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <header className="flex items-center justify-between mb-6" data-testid="app-header">
          <div>
            <h1 className="text-xl font-semibold tracking-tight text-slate-900">
              Quick Scan
            </h1>
            <div className="text-[11px] font-mono uppercase tracking-widest text-slate-400 mt-0.5">
              One-off run — not saved. Use Reports for daily tracking.
            </div>
          </div>
        </header>

        <div className="grid grid-cols-12 gap-6">
          {/* Left: Input Panel */}
          <aside className="col-span-12 lg:col-span-4 xl:col-span-3 space-y-6">
            <section
              className="bg-white border border-slate-200 rounded-xl p-5"
              data-testid="input-panel"
            >
              <div className="flex items-center gap-2 mb-4">
                <Sparkles size={16} className="text-pink-600" />
                <h3 className="text-sm font-bold tracking-tight text-slate-900">
                  New Extraction
                </h3>
              </div>

              <Tabs value={inputMode} onValueChange={setInputMode} className="mb-4">
                <TabsList className="grid grid-cols-2 bg-white border border-slate-200">
                  <TabsTrigger
                    value="paste"
                    className="data-[state=active]:bg-slate-900 data-[state=active]:text-black"
                    data-testid="input-tab-paste"
                  >
                    <ClipboardList size={13} className="mr-1.5" /> Paste
                  </TabsTrigger>
                  <TabsTrigger
                    value="upload"
                    className="data-[state=active]:bg-slate-900 data-[state=active]:text-black"
                    data-testid="input-tab-upload"
                  >
                    <Upload size={13} className="mr-1.5" /> Upload
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="paste" className="mt-4 space-y-4">
                  {/* Groups toolbar */}
                  <div className="flex items-center gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-mono rounded-md bg-white border border-slate-200 text-slate-700 hover:border-pink-300 hover:text-pink-600 transition-all"
                          data-testid="load-group-trigger"
                        >
                          <FolderOpen size={12} />
                          Load Group
                          <span className="text-pink-600">{groups.length}</span>
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="start"
                        className="bg-white border-slate-200 text-slate-800 min-w-[240px]"
                        data-testid="groups-dropdown-content"
                      >
                        <DropdownMenuLabel className="text-[10px] uppercase font-mono tracking-widest text-slate-400">
                          Saved Handle Groups
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator className="bg-slate-100" />
                        {groups.length === 0 && (
                          <div className="px-2 py-3 text-xs text-slate-400 text-center">
                            No groups saved yet
                          </div>
                        )}
                        {groups.map((g) => (
                          <div
                            key={g.id}
                            className="flex items-center justify-between gap-2 px-2 py-1.5 hover:bg-slate-50 rounded-md group"
                          >
                            <button
                              onClick={() => applyGroup(g)}
                              className="flex-1 text-left"
                              data-testid={`group-load-${g.id.slice(0, 8)}`}
                            >
                              <div className="text-xs font-medium text-slate-800 group-hover:text-pink-600">
                                {g.name}
                              </div>
                              <div className="text-[10px] font-mono text-slate-400">
                                {g.handles.length} handles
                                {g.keywords?.length ? ` · ${g.keywords.length} keywords` : ""}
                              </div>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteGroup(g.id, g.name);
                              }}
                              className="text-slate-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                              data-testid={`group-delete-${g.id.slice(0, 8)}`}
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>

                    <button
                      onClick={() => setGroupDialogOpen(true)}
                      disabled={!handles.length}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-mono rounded-md bg-white border border-slate-200 text-slate-700 hover:border-pink-300 hover:text-pink-600 transition-all disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:text-slate-700"
                      title={!handles.length ? "Add handles first" : "Save handles as a group"}
                      data-testid="save-group-button"
                    >
                      <Save size={12} />
                      Save as Group
                    </button>
                  </div>

                  <div>
                    <label className="text-[10px] uppercase font-mono tracking-widest text-slate-400 mb-1.5 block">
                      Instagram Handles
                      <span className="ml-2 text-pink-600">{handles.length}</span>
                    </label>
                    <Textarea
                      data-testid="handles-textarea"
                      value={handlesText}
                      onChange={(e) => setHandlesText(e.target.value)}
                      placeholder="@nike&#10;@adidas&#10;gymshark"
                      className="bg-white border-slate-200 text-sm font-mono resize-none h-24 focus:border-pink-300"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase font-mono tracking-widest text-slate-400 mb-1.5 block">
                      Keywords / #Hashtags / @Mentions
                      <span className="ml-2 text-pink-600">{keywords.length}</span>
                    </label>
                    <Textarea
                      data-testid="keywords-textarea"
                      value={keywordsText}
                      onChange={(e) => setKeywordsText(e.target.value)}
                      placeholder="#running&#10;airmax&#10;@justdoit"
                      className="bg-white border-slate-200 text-sm font-mono resize-none h-24 focus:border-pink-300"
                    />
                  </div>
                </TabsContent>

                <TabsContent value="upload" className="mt-4">
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragOver}
                    onDragLeave={handleDragLeave}
                    className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all bg-white ${
                      isDragging
                        ? "border-pink-300 bg-pink-50 scale-[1.02] amber-glow"
                        : "border-slate-200 hover:border-pink-300"
                    }`}
                    data-testid="file-upload-dropzone"
                  >
                    <Upload
                      size={26}
                      className={`mx-auto mb-3 transition-transform ${
                        isDragging ? "text-pink-600 -translate-y-1" : "text-pink-600"
                      }`}
                    />
                    <div className="text-sm text-slate-700 mb-1">
                      {isDragging ? "Release to upload…" : "Drop CSV/Excel or click to browse"}
                    </div>
                    <div className="text-[11px] text-slate-400 font-mono">
                      Columns: handle, keyword
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      className="hidden"
                      onChange={handleFileUpload}
                      data-testid="file-upload-input"
                    />
                  </div>
                  {(handles.length > 0 || keywords.length > 0) && (
                    <div className="mt-3 text-xs text-slate-500 font-mono">
                      Loaded {handles.length} handle(s), {keywords.length} keyword(s)
                    </div>
                  )}
                </TabsContent>
              </Tabs>

              <Separator className="my-4 bg-slate-100" />

              {/* Timeline */}
              <div className="mb-4">
                <label className="text-[10px] uppercase font-mono tracking-widest text-slate-400 mb-2 flex items-center gap-1.5">
                  <CalendarClock size={11} className="text-pink-600" />
                  Timeline
                </label>
                <div className="grid grid-cols-5 gap-1 mb-2" data-testid="timeline-presets">
                  {[
                    { id: "all", label: "All" },
                    { id: "7d", label: "7d" },
                    { id: "30d", label: "30d" },
                    { id: "90d", label: "90d" },
                    { id: "custom", label: "Custom" },
                  ].map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setTimelinePreset(p.id)}
                      className={`px-1.5 py-1.5 rounded-md text-[11px] font-medium transition-all ${
                        timelinePreset === p.id
                          ? "bg-slate-900 text-white"
                          : "bg-white border border-slate-200 text-slate-500 hover:border-pink-300"
                      }`}
                      data-testid={`timeline-preset-${p.id}`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                {timelinePreset === "custom" && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="text-[9px] font-mono uppercase tracking-widest text-slate-400 mb-1">
                        From
                      </div>
                      <DateField value={fromDate} onChange={setFromDate} testid="timeline-from-date" placeholder="From" className="h-9 w-full" />
                    </div>
                    <div>
                      <div className="text-[9px] font-mono uppercase tracking-widest text-slate-400 mb-1">
                        To
                      </div>
                      <DateField value={toDate} onChange={setToDate} testid="timeline-to-date" placeholder="To" className="h-9 w-full" />
                    </div>
                  </div>
                )}
                {timelinePreset !== "all" && computedRange.from_date && (
                  <div className="mt-2 text-[10px] font-mono text-slate-400">
                    {computedRange.from_date} → {computedRange.to_date || "today"}
                  </div>
                )}
              </div>

              {/* Match mode */}
              <div className="mb-4">
                <label className="text-[10px] uppercase font-mono tracking-widest text-slate-400 mb-2 block">
                  Match Mode
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setMatchMode("substring")}
                    className={`px-3 py-2 rounded-md text-xs font-medium transition-all ${
                      matchMode === "substring"
                        ? "bg-slate-900 text-white"
                        : "bg-white border border-slate-200 text-slate-500 hover:border-pink-300"
                    }`}
                    data-testid="match-mode-substring"
                  >
                    Substring
                  </button>
                  <button
                    onClick={() => setMatchMode("exact")}
                    className={`px-3 py-2 rounded-md text-xs font-medium transition-all ${
                      matchMode === "exact"
                        ? "bg-slate-900 text-white"
                        : "bg-white border border-slate-200 text-slate-500 hover:border-pink-300"
                    }`}
                    data-testid="match-mode-exact"
                  >
                    Exact
                  </button>
                </div>
              </div>

              {/* Posts limit */}
              <div className="mb-5">
                <label className="text-[10px] uppercase font-mono tracking-widest text-slate-400 mb-2 flex items-center justify-between">
                  Posts per Handle
                  <span className="text-pink-600 font-bold text-base">{postsLimit}</span>
                </label>
                <Slider
                  data-testid="posts-slider"
                  value={[postsLimit]}
                  min={1}
                  max={100}
                  step={1}
                  onValueChange={(v) => setPostsLimit(v[0])}
                  className="[&_[role=slider]]:bg-slate-900 [&_[role=slider]]:border-pink-300"
                />
                <div className="flex justify-between text-[10px] text-slate-400 font-mono mt-1">
                  <span>1</span>
                  <span>50</span>
                  <span>100</span>
                </div>
              </div>

              <Button
                data-testid="start-scraper-button"
                onClick={startScrape}
                disabled={isRunning || running}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold h-11 amber-glow active:scale-[0.98] transition-all"
              >
                {isRunning || running ? (
                  <>
                    <Loader2 className="mr-2 animate-spin" size={16} />
                    Scraping…
                  </>
                ) : (
                  <>
                    <Zap size={16} className="mr-2" />
                    Start Extraction
                  </>
                )}
              </Button>
            </section>

            {/* Job History */}
            <section
              className="bg-white border border-slate-200 rounded-xl p-5"
              data-testid="job-history-sidebar"
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold tracking-tight text-slate-900">
                  Recent Jobs
                </h3>
                <button onClick={loadJobs} className="text-slate-400 hover:text-pink-600" data-testid="refresh-jobs">
                  <RefreshCw size={13} />
                </button>
              </div>
              <ScrollArea className="h-56">
                <div className="space-y-2 pr-2">
                  {jobs.length === 0 && (
                    <div className="text-xs text-slate-400 font-mono py-3">No jobs yet</div>
                  )}
                  {jobs.map((j) => (
                    <button
                      key={j.id}
                      onClick={() => loadJob(j.id)}
                      className={`w-full text-left p-2.5 rounded-lg border transition-all ${
                        job?.id === j.id
                          ? "border-pink-300 bg-slate-50"
                          : "border-slate-200 bg-white hover:border-pink-200"
                      }`}
                      data-testid={`job-item-${j.id.slice(0, 8)}`}
                    >
                      <div className="flex items-center justify-between text-[10px] font-mono">
                        <span className="text-slate-400">{j.id.slice(0, 8)}</span>
                        <StatusBadge status={j.status} />
                      </div>
                      <div className="mt-1 text-xs text-slate-700 truncate">
                        {(j.handles || []).slice(0, 3).join(", ")}
                        {(j.handles || []).length > 3 && ` +${j.handles.length - 3}`}
                      </div>
                      <div className="mt-1 text-[10px] text-slate-400 font-mono">
                        {j.matched_count || 0} matched · {new Date(j.created_at).toLocaleString()}
                      </div>
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </section>

            {/* Active schedules */}
            {schedules.filter((s) => s.enabled).length > 0 && (
              <section
                className="bg-white border border-slate-200 rounded-xl p-5"
                data-testid="schedules-panel"
              >
                <div className="flex items-center gap-2 mb-3">
                  <CalendarClock size={14} className="text-emerald-600" />
                  <h3 className="text-sm font-bold tracking-tight text-slate-900">
                    Daily Schedules
                  </h3>
                  <Badge className="bg-emerald-50 text-emerald-600 border-emerald-200 text-[10px]">
                    10:00 IST
                  </Badge>
                </div>
                <div className="space-y-2">
                  {enabledSchedules
                    .map((s) => (
                      <div
                        key={s.id}
                        className="p-2.5 rounded-lg border border-slate-200 bg-white"
                        data-testid={`schedule-item-${s.id.slice(0, 8)}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="text-xs text-slate-800 truncate max-w-[140px]">
                            {(s.handles || []).slice(0, 2).join(", ")}
                            {(s.handles || []).length > 2 && ` +${s.handles.length - 2}`}
                          </div>
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={async () => {
                                try {
                                  const { data } = await axios.post(
                                    `${API}/scrape/schedule/${s.id}/run-now`
                                  );
                                  toast.success("Triggered a scheduled run");
                                  setTimeout(loadJobs, 1200);
                                } catch (e) {
                                  toast.error("Could not trigger");
                                }
                              }}
                              className="text-emerald-600 hover:text-emerald-600"
                              title="Run now"
                              data-testid={`schedule-run-now-${s.id.slice(0, 8)}`}
                            >
                              <PlayIcon size={13} />
                            </button>
                            <button
                              onClick={async () => {
                                try {
                                  await axios.delete(`${API}/scrape/schedule/${s.id}`);
                                  toast.success("Schedule disabled");
                                  loadSchedules();
                                } catch (e) {
                                  toast.error("Could not disable");
                                }
                              }}
                              className="text-slate-400 hover:text-red-600"
                              title="Disable"
                              data-testid={`schedule-delete-${s.id.slice(0, 8)}`}
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </div>
                        <div className="mt-1 text-[10px] font-mono text-slate-400">
                          {s.keywords?.length || 0} keywords · {s.posts_limit} posts/handle
                          {s.last_run_at && (
                            <>
                              {" · last "}
                              {new Date(s.last_run_at).toLocaleDateString()}
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </section>
            )}
          </aside>

          {/* Right: Main content */}
          <main className="col-span-12 lg:col-span-8 xl:col-span-9 space-y-6">
            {/* Progress panel */}
            {job && (
              <section
                className="bg-white border border-slate-200 rounded-xl p-5 fade-in-up"
                data-testid="live-progress-panel"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {job.status === "running" || job.status === "queued" ? (
                      <Loader2 className="animate-spin text-pink-600" size={16} />
                    ) : job.status === "completed" ? (
                      <span className="w-2 h-2 rounded-full bg-emerald-400" />
                    ) : (
                      <span className="w-2 h-2 rounded-full bg-red-500" />
                    )}
                    <h3 className="text-sm font-bold tracking-tight text-slate-900">
                      Job {job.id?.slice(0, 8)}
                    </h3>
                    <StatusBadge status={job.status} />
                  </div>
                  {job.status === "completed" && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button
                        onClick={toggleSchedule}
                        variant="outline"
                        className={`h-9 border-slate-200 font-medium ${
                          currentSchedule
                            ? "bg-emerald-50 border-emerald-200 text-emerald-600 hover:bg-emerald-100"
                            : "bg-white text-slate-700 hover:border-pink-300 hover:text-pink-600"
                        }`}
                        data-testid="schedule-daily-button"
                      >
                        <CalendarClock size={14} className="mr-1.5" />
                        {currentSchedule ? "Scheduled Daily · 10:00 IST" : "Schedule Daily 10 AM IST"}
                      </Button>
                      <Button
                        onClick={exportExcel}
                        className="bg-slate-900 hover:bg-slate-800 text-white font-semibold h-9"
                        data-testid="export-excel-button"
                      >
                        <Download size={14} className="mr-1.5" />
                        Export Excel
                      </Button>
                    </div>
                  )}
                </div>

                {(job.status === "running" || job.status === "queued") && (
                  <div className="h-1.5 bg-white rounded-full overflow-hidden mb-3">
                    <div className="h-full w-1/3 bg-gradient-to-r from-pink-500 to-pink-400 pulse-glow animate-pulse rounded-full" />
                  </div>
                )}

                <ScrollArea className="h-24 rounded-md bg-white border border-slate-200 p-3">
                  <div className="text-[11px] font-mono text-slate-500 space-y-1" data-testid="progress-log">
                    {(job.log || []).map((line, i) => (
                      <div key={i} className={line.startsWith("ERROR") ? "text-red-600" : ""}>
                        <span className="text-slate-400">›</span> {line}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </section>
            )}

            {/* Stats grid */}
            {job?.status === "completed" && (
              <section
                className="grid grid-cols-2 md:grid-cols-4 gap-4 fade-in-up"
                data-testid="stats-grid"
              >
                <StatCard label="Matched Posts" value={stats.matched} icon={Search} accent="amber" />
                <StatCard label="Unique Handles" value={stats.unique} icon={Users} accent="amber" />
                <StatCard label="Total Likes" value={formatNum(stats.likes)} icon={Heart} accent="pink" />
                <StatCard label="Total Comments" value={formatNum(stats.comments)} icon={MessageCircle} accent="purple" />
              </section>
            )}

            {/* Summary Table */}
            {job?.status === "completed" && summary.length > 0 && (
              <section
                className="bg-white border border-slate-200 rounded-xl overflow-hidden fade-in-up"
                data-testid="summary-table"
              >
                <div className="p-5 border-b border-slate-200">
                  <div className="flex items-center gap-2">
                    <Users size={16} className="text-pink-600" />
                    <h3 className="text-sm font-bold tracking-tight text-slate-900">
                      Influencer Summary
                    </h3>
                    <Badge className="bg-slate-50 text-slate-500 border-slate-200">
                      {summary.length}
                    </Badge>
                  </div>
                </div>
                {/* Posted/Not-Posted split — same block as the report page +
                    Excel Report Overview sheet. Stories don't count as posted. */}
                {summary.length > 0 && (
                  <div className="p-5 border-b border-slate-200">
                    <PostedBifurcation summary={summary} testIdPrefix="qs-bif" />
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-widest font-mono text-slate-400 border-b border-slate-200 bg-white">
                        <th className="text-left px-5 py-3">Handle</th>
                        <th className="text-right px-3 py-3">Followers</th>
                        <th className="text-right px-3 py-3">Profile Posts</th>
                        <th className="text-right px-3 py-3 text-pink-600">Matched</th>
                        <th className="text-right px-3 py-3 text-pink-600">Reels</th>
                        <th className="text-right px-3 py-3 text-violet-600">Carousels</th>
                        <th className="text-right px-3 py-3 text-emerald-600">Static</th>
                        <th className="text-right px-3 py-3 text-blue-400">Videos</th>
                        <th className="text-right px-3 py-3 text-fuchsia-600">Stories</th>
                        <th className="text-right px-3 py-3">Likes</th>
                        <th className="text-right px-3 py-3">Comments</th>
                        <th className="text-right px-3 py-3">Engagement</th>
                        <th className="text-right px-5 py-3">Video Views</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.map((s) => (
                        <tr key={s.handle} className="data-row border-b border-slate-200/50">
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-2">
                              {s.profile_pic_url ? (
                                <img
                                  src={s.profile_pic_url}
                                  alt=""
                                  referrerPolicy="no-referrer"
                                  className="w-7 h-7 rounded-full object-cover border border-slate-200"
                                />
                              ) : (
                                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-pink-500 to-pink-500 flex items-center justify-center text-[10px] font-bold text-black">
                                  {s.handle?.[0]?.toUpperCase()}
                                </div>
                              )}
                              <div>
                                <div className="text-slate-800 font-medium flex items-center gap-1">
                                  <a
                                    href={s.profile_url || `https://www.instagram.com/${s.handle}/`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:text-pink-600"
                                  >
                                    @{s.handle}
                                  </a>
                                  {s.verified && (
                                    <span
                                      title="Verified"
                                      className="text-blue-400 text-[11px]"
                                    >
                                      ✓
                                    </span>
                                  )}
                                </div>
                                {s.owner_full_name && (
                                  <div className="text-[10px] text-slate-400">{s.owner_full_name}</div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="text-right font-mono text-slate-800 font-semibold">
                            {formatNum(s.followers)}
                          </td>
                          <td className="text-right font-mono text-slate-500">
                            {formatNum(s.total_posts_on_profile)}
                          </td>
                          <td className="text-right font-mono text-pink-600 font-bold">{s.matched_posts}</td>
                          <td className="text-right font-mono text-pink-600">{s.reels}</td>
                          <td className="text-right font-mono text-violet-600">{s.carousels}</td>
                          <td className="text-right font-mono text-emerald-600">{s.static}</td>
                          <td className="text-right font-mono text-blue-400">{s.videos}</td>
                          <td className="text-right font-mono text-fuchsia-600" title={
                            `Highlights: ${s.stories_highlights || 0} · Live: ${s.stories_live || 0}`
                          }>{s.stories || 0}</td>
                          <td className="text-right font-mono text-slate-700">{formatNum(s.total_likes)}</td>
                          <td className="text-right font-mono text-slate-700">{formatNum(s.total_comments)}</td>
                          <td className="text-right font-mono text-pink-600">{formatNum(s.total_engagement)}</td>
                          <td className="px-5 text-right font-mono text-slate-700">{formatNum(s.total_video_views)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="p-3 border-t border-slate-200 text-[10px] font-mono text-slate-400">
                  Shares · Saves · Reach · Impressions require Instagram Insights API (owner-only) and are not available for public profiles.
                </div>
              </section>
            )}

            {/* Results Table */}
            {job?.status === "completed" && (
              <section
                className="bg-white border border-slate-200 rounded-xl overflow-hidden fade-in-up"
                data-testid="results-table"
              >
                <div className="p-5 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                  <div className="flex items-center gap-2">
                    <Filter size={16} className="text-pink-600" />
                    <h3 className="text-sm font-bold tracking-tight text-slate-900">
                      Matched Posts
                    </h3>
                    <Badge className="bg-slate-50 text-slate-500 border-slate-200">
                      {filteredResults.length}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="relative">
                      <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input
                        placeholder="Search…"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="h-9 pl-8 w-44 bg-white border-slate-200 text-xs"
                        data-testid="keyword-search-input"
                      />
                    </div>
                    <div className="flex gap-1 bg-white border border-slate-200 rounded-md p-0.5" data-testid="post-type-filter">
                      {["all", "Reel", "Carousel", "Static", "Video"].map((t) => (
                        <button
                          key={t}
                          onClick={() => setFilterType(t)}
                          className={`px-2.5 py-1 text-[11px] rounded font-medium transition ${
                            filterType === t
                              ? "bg-slate-900 text-white"
                              : "text-slate-500 hover:text-pink-600"
                          }`}
                          data-testid={`filter-${t.toLowerCase()}`}
                        >
                          {t === "all" ? "All" : t}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {filteredResults.length === 0 ? (
                  <div className="p-12 text-center text-slate-400 text-sm">
                    No matched posts found for the current filter.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-widest font-mono text-slate-400 border-b border-slate-200 bg-white">
                          <th className="text-left px-4 py-3">Thumb</th>
                          <th className="text-left px-3 py-3">Handle</th>
                          <th className="text-left px-3 py-3">Type</th>
                          <th className="text-left px-3 py-3">Keywords</th>
                          <th className="text-left px-3 py-3">Caption</th>
                          <th className="text-right px-3 py-3">
                            <Heart size={11} className="inline text-pink-600" /> Likes
                          </th>
                          <th className="text-right px-3 py-3">
                            <MessageCircle size={11} className="inline text-violet-600" /> Comm.
                          </th>
                          <th className="text-right px-3 py-3">
                            <Eye size={11} className="inline text-blue-400" /> Views
                          </th>
                          <th className="text-left px-3 py-3">Date</th>
                          <th className="text-right px-4 py-3">Open</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredResults.map((p, i) => (
                          <tr
                            key={p.shortcode || i}
                            className="data-row border-b border-slate-200/50 align-top"
                            data-testid={`result-row-${i}`}
                          >
                            <td className="px-4 py-3">
                              {p.thumbnail ? (
                                <img
                                  src={p.thumbnail}
                                  alt=""
                                  className="w-12 h-12 object-cover rounded-md border border-slate-200"
                                  loading="lazy"
                                  referrerPolicy="no-referrer"
                                  onError={(e) => (e.currentTarget.style.opacity = "0.2")}
                                />
                              ) : (
                                <div className="w-12 h-12 bg-slate-50 rounded-md border border-slate-200 flex items-center justify-center">
                                  <ImageIcon size={16} className="text-slate-400" />
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-3">
                              <div className="text-slate-800 font-medium">@{p.handle}</div>
                            </td>
                            <td className="px-3 py-3">
                              <PostTypePill type={p.post_type} />
                            </td>
                            <td className="px-3 py-3 max-w-[180px]">
                              <div className="flex flex-wrap">
                                {(p.matched_keywords || []).map((k) => (
                                  <span className="kw-chip" key={k}>
                                    {k}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="px-3 py-3 max-w-[300px]">
                              <button
                                onClick={() => setCaptionPost(p)}
                                className="text-left w-full group"
                                data-testid={`caption-open-${i}`}
                              >
                                <div className="text-xs text-slate-700 line-clamp-3 group-hover:text-pink-600 transition-colors">
                                  {p.caption || (
                                    <span className="text-slate-400 italic">no caption</span>
                                  )}
                                </div>
                                {p.caption && p.caption.length > 90 && (
                                  <span className="text-[10px] font-mono text-pink-600/60 group-hover:text-pink-600 mt-1 inline-block">
                                    view full →
                                  </span>
                                )}
                              </button>
                            </td>
                            <td className="px-3 py-3 text-right font-mono text-pink-600">{formatNum(p.likes)}</td>
                            <td className="px-3 py-3 text-right font-mono text-violet-600">
                              {formatNum(p.comments)}
                            </td>
                            <td className="px-3 py-3 text-right font-mono text-blue-400">
                              {p.video_views ? formatNum(p.video_views) : "—"}
                            </td>
                            <td className="px-3 py-3 text-xs text-slate-500 font-mono whitespace-nowrap">
                              {p.post_date ? new Date(p.post_date).toLocaleDateString() : "—"}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <a
                                href={p.post_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex text-pink-600 hover:text-pink-600"
                                data-testid={`open-post-${i}`}
                              >
                                <ExternalLink size={14} />
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            )}

            {/* Empty state */}
            {!job && (
              <section className="bg-white border border-dashed border-slate-200 rounded-xl p-12 text-center">
                <div className="inline-flex p-4 rounded-full bg-slate-50 mb-4">
                  <Instagram size={28} className="text-pink-600" />
                </div>
                <h2 className="text-xl font-bold text-slate-800 mb-2">Ready to hunt keywords</h2>
                <p className="text-sm text-slate-400 max-w-md mx-auto">
                  Drop Instagram handles and the keywords, hashtags, or mentions you want to track.
                  CultureX scrapes recent posts via Apify and returns every match ready for Excel.
                </p>
                <div className="flex items-center justify-center gap-6 mt-6 text-[11px] font-mono text-slate-400 uppercase tracking-widest">
                  <span className="flex items-center gap-1.5"><Hash size={11} className="text-pink-600"/> Hashtags</span>
                  <span className="flex items-center gap-1.5"><AtSign size={11} className="text-pink-600"/> Mentions</span>
                  <span className="flex items-center gap-1.5"><ClipboardList size={11} className="text-pink-600"/> Captions</span>
                </div>
              </section>
            )}
          </main>
        </div>

        <footer className="mt-10 text-center text-[11px] font-mono text-slate-400 tracking-widest uppercase">
          Powered by Apify · Built with intent
        </footer>
      </div>

      {/* Caption preview dialog */}
      <Dialog open={!!captionPost} onOpenChange={(o) => !o && setCaptionPost(null)}>
        <DialogContent
          className="bg-white border-slate-200 text-slate-900 max-w-2xl"
          data-testid="caption-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <PostTypePill type={captionPost?.post_type} />
              <span>@{captionPost?.handle}</span>
            </DialogTitle>
            <DialogDescription className="text-slate-400 font-mono text-[11px]">
              {captionPost?.post_date
                ? new Date(captionPost.post_date).toLocaleString()
                : ""}
            </DialogDescription>
          </DialogHeader>
          {captionPost && (
            <div className="space-y-4">
              {captionPost.thumbnail && (
                <img
                  src={captionPost.thumbnail}
                  alt=""
                  className="w-full max-h-72 object-cover rounded-lg border border-slate-200"
                  referrerPolicy="no-referrer"
                />
              )}

              <div>
                <div className="text-[10px] uppercase font-mono tracking-widest text-slate-400 mb-2">
                  Matched Keywords
                </div>
                <div className="flex flex-wrap">
                  {(captionPost.matched_keywords || []).map((k) => (
                    <span key={k} className="kw-chip">
                      {k}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-[10px] uppercase font-mono tracking-widest text-slate-400 mb-2">
                  Caption
                </div>
                <ScrollArea className="max-h-56 rounded-md bg-white border border-slate-200 p-3">
                  <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
                    {captionPost.caption || (
                      <span className="text-slate-400 italic">No caption on this post.</span>
                    )}
                  </p>
                </ScrollArea>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase font-mono tracking-widest text-slate-400 mb-2 flex items-center gap-1">
                    <Hash size={10} /> Hashtags
                    <span className="text-pink-600 ml-1">
                      {captionPost.hashtags?.length || 0}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(captionPost.hashtags || []).length ? (
                      captionPost.hashtags.map((h) => (
                        <span
                          key={h}
                          className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-slate-50 text-pink-600 border border-slate-200"
                        >
                          #{h}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-slate-400">none</span>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase font-mono tracking-widest text-slate-400 mb-2 flex items-center gap-1">
                    <AtSign size={10} /> Mentions
                    <span className="text-pink-600 ml-1">
                      {captionPost.mentions?.length || 0}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(captionPost.mentions || []).length ? (
                      captionPost.mentions.map((m) => (
                        <span
                          key={m}
                          className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-slate-50 text-violet-600 border border-slate-200"
                        >
                          @{m}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-slate-400">none</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 pt-2">
                <div className="bg-white border border-slate-200 rounded-lg p-3">
                  <div className="text-[10px] uppercase font-mono tracking-widest text-slate-400">
                    Likes
                  </div>
                  <div className="text-lg font-mono font-bold text-pink-600 mt-1">
                    {formatNum(captionPost.likes)}
                  </div>
                </div>
                <div className="bg-white border border-slate-200 rounded-lg p-3">
                  <div className="text-[10px] uppercase font-mono tracking-widest text-slate-400">
                    Comments
                  </div>
                  <div className="text-lg font-mono font-bold text-violet-600 mt-1">
                    {formatNum(captionPost.comments)}
                  </div>
                </div>
                <div className="bg-white border border-slate-200 rounded-lg p-3">
                  <div className="text-[10px] uppercase font-mono tracking-widest text-slate-400">
                    Views
                  </div>
                  <div className="text-lg font-mono font-bold text-blue-400 mt-1">
                    {captionPost.video_views ? formatNum(captionPost.video_views) : "—"}
                  </div>
                </div>
              </div>

              <a
                href={captionPost.post_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-pink-600 hover:text-pink-600 font-mono"
                data-testid="caption-open-post"
              >
                Open on Instagram <ExternalLink size={13} />
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Save Group dialog */}
      <Dialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen}>
        <DialogContent
          className="bg-white border-slate-200 text-slate-900 max-w-md"
          data-testid="save-group-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Save size={16} className="text-pink-600" />
              Save as Handle Group
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">
              Give this list of {handles.length} handle(s) a name so you can load it in one click later.
              Keywords will also be saved if provided.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase font-mono tracking-widest text-slate-400 mb-1.5 block">
                Group Name
              </label>
              <Input
                autoFocus
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="e.g., Fitness Creators"
                className="bg-white border-slate-200 font-mono"
                onKeyDown={(e) => e.key === "Enter" && saveGroup()}
                data-testid="group-name-input"
              />
            </div>
            <div className="text-[11px] font-mono text-slate-400">
              {handles.slice(0, 5).map((h) => (
                <span key={h} className="kw-chip">
                  @{h}
                </span>
              ))}
              {handles.length > 5 && (
                <span className="text-slate-400 ml-1">+{handles.length - 5} more</span>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setGroupDialogOpen(false)}
                className="bg-white border-slate-200 hover:bg-slate-50 text-slate-700"
                data-testid="group-save-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={saveGroup}
                className="bg-slate-900 hover:bg-slate-800 text-white font-semibold"
                data-testid="group-save-confirm"
              >
                <Save size={13} className="mr-1.5" />
                Save Group
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function formatNum(n) {
  if (n == null) return "0";
  const num = Number(n);
  if (num >= 1e6) return (num / 1e6).toFixed(1) + "M";
  if (num >= 1e3) return (num / 1e3).toFixed(1) + "K";
  return String(num);
}

function StatusBadge({ status }) {
  const map = {
    completed: { color: "text-emerald-600 border-emerald-200 bg-emerald-50", label: "Completed" },
    running: { color: "text-pink-600 border-pink-200 bg-pink-50", label: "Running" },
    queued: { color: "text-blue-400 border-blue-500/30 bg-blue-500/10", label: "Queued" },
    failed: { color: "text-red-600 border-red-200 bg-red-50", label: "Failed" },
  };
  const c = map[status] || { color: "text-slate-500 border-gray-500/30 bg-gray-500/10", label: status };
  return (
    <span className={`text-[10px] uppercase tracking-widest font-mono px-2 py-0.5 rounded border ${c.color}`}>
      {c.label}
    </span>
  );
}
